/**
 * The value arithmetic and the focus/format transitions of the owned `NumberInput`, as plain functions
 * (INDEX §6.5 kind 1); the component only holds the state and applies what these return.
 *
 * Mantine 6's NumberInput owned this pipeline; Mantine 7 replaced it with react-number-format, which
 * has no `formatter`/`parser` pair, no `precision`, and clamps on blur instead of on every change
 * (SC-2703). Every step below is Mantine 6.0.19's `NumberInput.js` line for line, so the value a
 * keystroke emits and the text the field shows are unchanged. The one omission: Mantine 6 also ran
 * `replaceAll(thousandsSeparator)` with the separator unset, which stripped the literal text "undefined"
 * from typed input.
 */

/** Mantine 6's NumberInput value contract: the empty string is the cleared state. */
export type NumberInputValue = number | "";

export interface NumberInputParseOptions {
  precision: number;
  min: number | undefined;
  max: number | undefined;
  parser: (value: string) => string;
}

export interface NumberInputFormatOptions {
  precision: number;
  removeTrailingZeros: boolean;
  formatter: (value: string) => string;
}

export interface NumberInputStepOptions {
  step: number;
  precision: number;
  min: number | undefined;
  max: number | undefined;
}

/** Mantine 6's `defaultFormatter`. */
export const defaultNumberInputFormatter = (value: string): string => value || "";

/** Mantine 6's `defaultParser`: a lone minus passes through, anything unparseable clears. */
export const defaultNumberInputParser = (value: string): string => {
  if (value === "-") {
    return value;
  }

  const parsed = parseFloat(value[0] === "." ? `0${value}` : value);
  return Number.isNaN(parsed) ? "" : value;
};

/** Mantine 6's `clamp` with its `-Infinity`/`Infinity` stand-ins for an unset bound; NaN stays NaN. */
const clamp = (value: number, min: number | undefined, max: number | undefined): number =>
  Math.min(Math.max(value, min ?? -Infinity), max ?? Infinity);

/** Mantine 6's `parsePrecision`: fixed-point text at `precision` places, trailing zeros optionally dropped. */
const toPrecisionText = (value: NumberInputValue, precision: number, removeTrailingZeros = false): string => {
  if (value === "") {
    return "";
  }

  let result = value.toFixed(precision);
  if (removeTrailingZeros && precision > 0) {
    result = result.replace(new RegExp(`[0]{0,${precision}}$`), "");
    if (result.endsWith(".")) {
      result = result.slice(0, -1);
    }
  }

  return result;
};

/** The text shown for a value while the field is not focused (Mantine 6's `formatInternalValue`). */
export const formatNumberInputValue = (
  value: NumberInputValue,
  { precision, removeTrailingZeros, formatter }: NumberInputFormatOptions,
): string => formatter(toPrecisionText(value, precision, removeTrailingZeros));

/**
 * The value a change to the typed text emits (Mantine 6's `processInputValue`): parsed, rounded to
 * `precision`, clamped to `min`/`max` on every keystroke, and `""` when nothing numeric remains.
 */
export const parseNumberInputText = (text: string, { precision, min, max, parser }: NumberInputParseOptions): NumberInputValue => {
  const normalized = text[0] === "." ? `0${text}` : text;
  const clamped = clamp(parseFloat(toPrecisionText(parseFloat(parser(normalized)), precision)), min, max);

  return Number.isNaN(clamped) ? "" : clamped;
};

/**
 * The value an arrow key or step control emits (Mantine 6's increment/decrement). A cleared field
 * starts from `min`, else 0 — Mantine 6's `startValue` came first, and no owned prop carries one.
 */
export const stepNumberInputValue = (
  value: NumberInputValue,
  direction: 1 | -1,
  { step, precision, min, max }: NumberInputStepOptions,
): number => {
  if (value === "") {
    return min ?? 0;
  }

  return parseFloat(toPrecisionText(clamp(value + direction * step, min, max), precision));
};

/** The owned NumberInput's two pieces of state: the value it holds and the text the field shows. */
export interface NumberInputState {
  internalValue: NumberInputValue;
  inputValue: string;
}

/** A transition's next state, and whether it calls `onChange` with that state's `internalValue`. */
export interface NumberInputTransition {
  state: NumberInputState;
  emit: boolean;
}

/** What every transition reads besides the state: whether the field has focus, and its display format. */
export interface NumberInputFieldOptions {
  focused: boolean;
  format: (value: NumberInputValue) => string;
}

/**
 * The text Mantine 6's `setInternalValue` wrote for a new value: the formatted value, but only while the
 * field is unfocused or when forced, and only when it differs from the text of the render it ran in.
 * `undefined` means it wrote nothing — which is how typing keeps its raw text while focused.
 */
const reformattedText = (
  state: NumberInputState,
  next: NumberInputValue,
  { focused, format }: NumberInputFieldOptions,
  force: boolean,
): string | undefined => {
  if (focused && !force) {
    return undefined;
  }

  const formatted = format(next);
  return formatted === state.inputValue ? undefined : formatted;
};

/**
 * A change to the typed text (Mantine 6's `processInputValue`). The field shows the typed text as it is,
 * and emits only when the parsed value changed. Mantine 6 wrote the typed text first and then ran
 * `setInternalValue`, so an unfocused change (autofill) ends on the formatted value — unless that equals
 * the text already shown, in which case the typed text stays.
 */
export const changeNumberInputState = (
  state: NumberInputState,
  text: string,
  options: NumberInputFieldOptions & NumberInputParseOptions,
): NumberInputTransition => {
  const next = parseNumberInputText(text, options);

  return {
    state: { internalValue: next, inputValue: reformattedText(state, next, options, false) ?? text },
    emit: next !== state.internalValue,
  };
};

/**
 * An arrow key or step control (Mantine 6's increment/decrement): the stepped value, formatted even while
 * focused, and always emitted — also when a bound leaves the value where it was.
 */
export const stepNumberInputState = (
  state: NumberInputState,
  direction: 1 | -1,
  options: NumberInputFieldOptions & NumberInputStepOptions,
): NumberInputTransition => {
  const next = stepNumberInputValue(state.internalValue, direction, options);

  return { state: { internalValue: next, inputValue: reformattedText(state, next, options, true) ?? state.inputValue }, emit: true };
};

/**
 * The re-sync Mantine 6 ran whenever `value` or focus changed: nothing while the field is focused, so the
 * typed text stays; otherwise the controlled `value` (or, uncontrolled, the held value) formatted. Focus
 * leaving the field runs it, which is the reformat on blur. It emits nothing.
 */
export const syncNumberInputState = (
  state: NumberInputState,
  value: NumberInputValue | undefined,
  options: NumberInputFieldOptions,
): NumberInputState => {
  if (options.focused) {
    return state;
  }

  const next = value === undefined ? state.internalValue : value;
  return { internalValue: next, inputValue: reformattedText(state, next, options, true) ?? state.inputValue };
};

/** The step a key press takes (Mantine 6's `handleKeyDown`): ArrowUp and ArrowDown, unless the field is read-only. */
export const getNumberInputKeyStep = (key: string, readOnly: boolean | undefined): 1 | -1 | undefined => {
  if (readOnly) {
    return undefined;
  }

  return key === "ArrowUp" ? 1 : key === "ArrowDown" ? -1 : undefined;
};

/** Mantine 6 ignored a change fired mid IME composition. */
export const isComposingNumberInputChange = (nativeEvent: Event): boolean =>
  "isComposing" in nativeEvent && nativeEvent.isComposing === true;

/** Mantine 6's `getInputMode`, which picks the virtual keyboard from the step sign and precision. */
export const getNumberInputMode = (step: number, precision: number, os: string): "numeric" | "decimal" | "text" => {
  if (Number.isInteger(step) && step >= 0 && precision === 0) {
    return "numeric";
  }
  if (!Number.isInteger(step) && step >= 0 && precision !== 0) {
    return "decimal";
  }
  if (Number.isInteger(step) && step < 0 && precision === 0) {
    return os === "ios" ? "text" : "decimal";
  }
  if (!Number.isInteger(step) && step < 0 && precision !== 0) {
    return os === "ios" ? "text" : "decimal";
  }

  return "numeric";
};
